// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Observation, QuestionnaireResponse } from '@medplum/fhirtypes';

/**
 * Maps sex-at-birth questionnaire coding codes to a normalized sex category
 * used for clinical scoring (e.g. STOP-BANG criterion G).
 *
 * Codes from public-questionnaires.json sex-at-birth answerOptions.
 */
export const SEX_AT_BIRTH_CODE_MAP: Record<string, 'male' | 'female' | 'other' | 'unknown'> = {
  // v3-AdministrativeGender (http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender)
  M: 'male',
  F: 'female',
  // v3-NullFlavor (http://terminology.hl7.org/CodeSystem/v3-NullFlavor)
  OTH: 'other', // other
  ASKU: 'unknown', // asked but unknown
  UNK: 'unknown', // unknown
  // data-absent-reason (http://terminology.hl7.org/CodeSystem/data-absent-reason)
  'asked-declined': 'unknown', // asked but declined
};

// Yes/No linkIds from the patient-facing STOP-BANG questionnaire
const STOP_BANG_YES_NO_LINK_IDS = [
  'snore-loudly',
  'tired-fatigued',
  'stop-breathing',
  'high-blood-pressure',
  'age-50-or-older',
  'neck-circumference-over-16',
] as const;
const LEGACY_NECK_LINK_ID = 'neck-collar-size';

const ESS_LINK_IDS = [
  'ess-reading',
  'ess-television',
  'ess-inactive',
  'ess-passenger',
  'ess-lying-down',
  'ess-talking',
  'ess-after-meal',
  'ess-traffic',
] as const;

export const STOP_BANG_MAX_SCORE = 8;
export const EPWORTH_MAX_SCORE = 24;

/**
 * Parse BMI from imperial height string (e.g. "5'11\"") and weight in pounds.
 * Returns undefined if parsing fails.
 */
function parseBmiFromImperial(heightStr: string, weightLbs: number): number | undefined {
  const match = heightStr.match(/(\d+)'(\d+)/);
  if (!match) {
    return undefined;
  }
  const feet = parseInt(match[1], 10);
  const inches = parseInt(match[2], 10);
  const heightCm = (feet * 12 + inches) * 2.54;
  const weightKg = weightLbs * 0.453592;
  const heightM = heightCm / 100;
  if (heightM === 0) {
    return undefined;
  }
  return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
}

/**
 * Calculate the STOP-BANG score from a QuestionnaireResponse.
 *
 * Supports the patient-facing questionnaire format:
 * - Yes/No questions answered with valueString "Yes"/"No"
 * - Height answered with valueString (e.g. "5'11\"")
 * - Weight answered with valueDecimal in pounds
 * - BMI criterion (B): BMI > 35 kg/m² = 1 point (derived from height + weight)
 * - Sex criterion (G): male sex at birth = 1 point (pass sex-at-birth code separately)
 *
 * @param sexAtBirth - Sex-at-birth code from the us-core-birthsex extension (e.g. "M", "F")
 * @returns Score from 0 to 8
 */
export function calculateStopBangScoreFromQR(qr: QuestionnaireResponse, sexAtBirth?: string): number {
  const yesNoAnswers: Record<string, string | boolean> = {};
  let heightStr: string | undefined;
  let weightLbs: number | undefined;

  for (const item of qr.item ?? []) {
    if (!item.linkId) {
      continue;
    }
    const answer = item.answer?.[0];
    if (!answer) {
      continue;
    }
    if (answer.valueBoolean != null) {
      yesNoAnswers[item.linkId] = answer.valueBoolean;
    } else if (answer.valueString != null) {
      yesNoAnswers[item.linkId] = answer.valueString;
    }
    if (item.linkId === 'height' && answer.valueString != null) {
      heightStr = answer.valueString;
    }
    if (item.linkId === 'weight' && answer.valueDecimal != null) {
      weightLbs = answer.valueDecimal;
    }
  }

  let score = STOP_BANG_YES_NO_LINK_IDS.reduce((s, linkId) => {
    const val =
      yesNoAnswers[linkId] ?? (linkId === 'neck-circumference-over-16' ? yesNoAnswers[LEGACY_NECK_LINK_ID] : undefined);
    return s + (val === true || val === 'Yes' ? 1 : 0);
  }, 0);

  // BMI criterion (B): BMI > 35 kg/m² scores 1 point
  if (heightStr !== undefined && weightLbs !== undefined) {
    const bmi = parseBmiFromImperial(heightStr, weightLbs);
    if (bmi !== undefined && bmi > 35) {
      score++;
    }
  }

  // Sex criterion (G): male sex at birth scores 1 point
  if (sexAtBirth != null && SEX_AT_BIRTH_CODE_MAP[sexAtBirth] === 'male') {
    score++;
  }

  return score;
}

/**
 * Calculate the Epworth Sleepiness Scale score from pre-parsed answers.
 *
 * @param answers - ESS questionnaire answers keyed by linkId
 * @returns Score from 0 to 24
 */
export function calculateEpworthScore(
  answers: Record<string, { valueInteger?: number; valueCoding?: { code?: string } }>
): number {
  return ESS_LINK_IDS.reduce((score, linkId) => {
    const answer = answers[linkId];
    if (answer?.valueInteger != null) {
      return score + answer.valueInteger;
    }
    if (answer?.valueCoding?.code != null) {
      const num = parseInt(answer.valueCoding.code, 10);
      if (!isNaN(num)) {
        return score + num;
      }
    }
    return score;
  }, 0);
}

/**
 * Calculate BMI from the STOP-BANG QuestionnaireResponse.
 * Extracts height (imperial string, e.g. "5'11\"") and weight (decimal in lbs).
 *
 * @returns BMI rounded to 1 decimal place, or undefined if height/weight are missing
 */
export function calculateBmiFromStopBangQR(qr: QuestionnaireResponse): number | undefined {
  let heightStr: string | undefined;
  let weightLbs: number | undefined;

  for (const item of qr.item ?? []) {
    if (item.linkId === 'height' && item.answer?.[0]?.valueString != null) {
      heightStr = item.answer[0].valueString;
    }
    if (item.linkId === 'weight' && item.answer?.[0]?.valueDecimal != null) {
      weightLbs = item.answer[0].valueDecimal;
    }
  }

  if (heightStr === undefined || weightLbs === undefined) {
    return undefined;
  }

  return parseBmiFromImperial(heightStr, weightLbs);
}

/**
 * Calculate BMI from height (cm) and weight (kg) FHIR Observation resources.
 * Returns undefined if either observation is missing.
 *
 * @returns BMI rounded to 1 decimal place, or undefined
 */
export function calculateBmiFromObservations(
  heightObs: Observation | undefined,
  weightObs: Observation | undefined
): number | undefined {
  const heightCm = heightObs?.valueQuantity?.value;
  const weightKg = weightObs?.valueQuantity?.value;

  if (heightCm == null || weightKg == null || heightCm === 0) {
    return undefined;
  }

  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return Math.round(bmi * 10) / 10;
}
